<script setup lang="ts">
import { Plus, EditPencil, DesignPencil, Trash } from "@iconoir/vue";
import ListPage from "~/components/templates/ListPage.vue";
import ListEmpty from "~/components/templates/ListEmpty.vue";
import SiteTemplateModal from "./SiteTemplateModal.vue";
import { AdminPermission } from "~~/schemas/admin-role";

defineOptions({ name: "SiteTemplate" });

const { t } = useI18n();
const toast = useToast();
const alert = useAlert();
const { can } = useAccess();
const { set: setTitle } = usePageTitle();

const modalRef = ref<InstanceType<typeof SiteTemplateModal> | null>(null);
const editingRowId = ref<string | null>(null);

const defaultFilter = {
  keyword: "",
  status: "all",
  category: "",
  accessLevel: "",
  page: 1,
  pageSize: 20,
};

const {
  firstLoading, listLoading, load, itemCount, pageCount, items, filter,
  isEmpty, isSearchEmpty, search, searchDebounced,
} = useList<any, typeof defaultFilter>({
  endpoint: "/site-templates",
  defaultFilter,
});

const { del, errorMsg } = useCrud<any>({ endpoint: "/site-templates" });

async function edit(row: any) {
  if (editingRowId.value) return;
  editingRowId.value = row.id;
  await modalRef.value?.open(row);
  editingRowId.value = null;
}

async function remove(row: any) {
  const ok = await alert.negative({
    title: t("siteTemplate.deleteConfirmTitle"),
    description: t("siteTemplate.deleteConfirmBody", { name: row.name }),
    confirmLabel: t("action.delete"),
    cancelLabel: t("action.cancel"),
  });
  if (!ok) return;
  try {
    await del(row.id);
    toast.success({ title: t("message.deleteSuccess") });
    await load();
  } catch {
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  }
}

const statusOptions = computed(() => [
  { value: "all", label: t("siteTemplate.statusFilter") },
  { value: "deleted", label: t("siteTemplate.statusDeleted") },
]);

const categoryOptions = computed(() => [
  { value: "", label: t("siteTemplate.categoryFilter") },
  { value: "conference", label: t("siteTemplate.categoryConference") },
  { value: "workshop", label: t("siteTemplate.categoryWorkshop") },
  { value: "festival", label: t("siteTemplate.categoryFestival") },
  { value: "exhibition", label: t("siteTemplate.categoryExhibition") },
  { value: "wedding", label: t("siteTemplate.categoryWedding") },
  { value: "trade-show", label: t("siteTemplate.categoryTradeShow") },
  { value: "concert", label: t("siteTemplate.categoryConcert") },
  { value: "sports-event", label: t("siteTemplate.categorySportsEvent") },
  { value: "fundraiser", label: t("siteTemplate.categoryFundraiser") },
  { value: "networking", label: t("siteTemplate.categoryNetworking") },
  { value: "meetup", label: t("siteTemplate.categoryMeetup") },
  { value: "other", label: t("siteTemplate.categoryOther") },
]);

const accessLevelOptions = computed(() => [
  { value: "", label: t("siteTemplate.accessLevelFilter") },
  { value: "public", label: t("siteTemplate.accessPublic") },
  { value: "private", label: t("siteTemplate.accessPrivate") },
  { value: "protected", label: t("siteTemplate.accessProtected") },
]);

function categoryLabel(value: string) {
  return categoryOptions.value.find((option) => option.value === value)?.label || value || "—";
}

function accessLevelLabel(value: string) {
  return accessLevelOptions.value.find((option) => option.value === value)?.label || value || "—";
}

onMounted(async () => {
  setTitle(t("siteTemplate.title"));
  await load();
});

watch(() => filter.keyword, () => searchDebounced());
watch(() => filter.status, () => search());
watch(() => filter.category, () => search());
watch(() => filter.accessLevel, () => search());
watch(() => filter.page, () => load());
</script>

<template>
  <ListPage :title="t('siteTemplate.title')" :first-loading="firstLoading" :is-empty="isEmpty"
    :is-search-empty="isSearchEmpty">
    <template #status>
      {{ listLoading ? t("ui.loading") : `${itemCount} ${t('siteTemplate.itemsSuffix')}` }}
    </template>

    <template #actions>
      <Button
        v-if="can(AdminPermission.SiteTemplatesWrite)"
        variant="primary"
        size="small"
        @click="modalRef?.open()"
      >
        <Plus class="icon" />
        {{ t("siteTemplate.add") }}
      </Button>
    </template>

    <template #empty>
      <ListEmpty :title="t('siteTemplate.emptyTitle')" :subtitle="t('siteTemplate.emptySubtitle')">
        <template #action>
          <Button v-if="can(AdminPermission.SiteTemplatesWrite)" variant="primary" size="small" @click="modalRef?.open()">
            <Plus class="icon" />
            {{ t("siteTemplate.add") }}
          </Button>
        </template>
      </ListEmpty>
    </template>

    <template #search-empty>
      <ListEmpty :title="t('siteTemplate.emptySearchTitle')" :subtitle="t('siteTemplate.emptySearchSubtitle')" />
    </template>

    <template #filter-left>
      <div class="flex flex-col md:flex-row items-center gap-small flex-1">
        <FormSelect v-model="filter.status" :options="statusOptions" size="sm" class="w-full md:flex-1" />
        <FormSelect v-model="filter.category" :options="categoryOptions" size="sm" class="w-full md:flex-1" />
        <FormSelect v-model="filter.accessLevel" :options="accessLevelOptions" size="sm" class="w-full md:flex-1" />
      </div>
    </template>

    <template #filter-right>
      <FormSearch v-model="filter.keyword" :placeholder="t('field.search')" size="sm" class="w-full md:w-1/2" />
    </template>

    <template #default>
      <DataTable :columns="[
        { key: 'title', label: t('siteTemplate.columnName'), class: 'md:min-w-[240px]' },
        { key: 'category', label: t('siteTemplate.columnCategory'), class: 'min-w-[112px] hidden md:table-cell' },
        { key: 'isActive', label: t('siteTemplate.columnActive'), class: 'min-w-[112px] hidden md:table-cell' },
        { key: 'actions', align: 'right', label: t('ui.action'), class: 'min-w-[80px]' },
      ]" :items="items" :actions="[]">
        <template #cell-title="{ row }">
          <img :src="row.featuredAsset?.url || 'https://picsum.photos/200/300?grayscale'" :alt="row.name" />
          <span class="font-semibold">{{ row.name }}</span>
          <Badge v-if="row.accessLevel === 'private'" intent="neutral" emphasis="medium" size="small">
            {{ accessLevelLabel(row.accessLevel) }}
          </Badge>
        </template>

        <template #cell-category="{ row }">
          <span>{{ categoryLabel(row.category) }}</span>
        </template>

        <template #cell-isActive="{ row }">
          <Badge :label="row.isActive ? t('status.active') : t('status.inactive')"
            :intent="row.isActive ? 'positive' : 'neutral'" emphasis="medium" size="small" />
        </template>

        <template #actions="{ row }">
          <div class="flex items-center justify-end gap-xsmall">
            <ButtonIcon
              v-if="can(AdminPermission.SiteTemplatesWrite)"
              variant="neutral-flat"
              size="small"
              :title="t('siteTemplate.openBuilder')"
              @click.stop="navigateTo(`/site-template/${row.id}/builder`)"
            >
              <DesignPencil class="icon" />
            </ButtonIcon>
            <ButtonIcon
              v-if="can(AdminPermission.SiteTemplatesWrite)"
              variant="neutral-flat"
              size="small"
              :title="t('action.edit')"
              :disabled="editingRowId === row.id"
              @click.stop="edit(row)"
            >
              <ProgressCircle v-if="editingRowId === row.id" size="small" />
              <EditPencil v-else class="icon" />
            </ButtonIcon>
            <ButtonIcon
              v-if="can(AdminPermission.SiteTemplatesDelete)"
              variant="neutral-flat"
              size="small"
              :title="t('action.delete')"
              @click.stop="remove(row)"
            >
              <Trash class="icon" />
            </ButtonIcon>
          </div>
        </template>
      </DataTable>

      <Pagination v-if="pageCount > 1" :count="itemCount" :page-size="filter.pageSize" v-model:page="filter.page" />
    </template>
  </ListPage>

  <SiteTemplateModal ref="modalRef" @saved="load()" />
</template>